import { Component, EventEmitter, Inject, Input, OnChanges, Optional, Output, SimpleChanges } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { TypeaheadService } from '@app/services/typeahead.service';

@GridModalComponent('materialgrade')
@Component({
    selector: 'tt-material-grade',
    templateUrl: './material-grade.component.html',
    styleUrls: ['./material-grade.component.css'],
})
export class MaterialGradeComponent implements OnChanges {
    @Input()
    public params?: MaterialGradeParams;

    public get?: MaterialGradeGet;

    @Output()
    public getLoaded = new EventEmitter<MaterialGradeGet>();

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public saving = false;

    public deleting = false;

    constructor(
        private datatask: DataTaskService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        @Optional() @Inject(TT_STATE_DIALOG) private state: StateDialogContext,
    ) {
        this.initFromDialog(state);
    }

    public searchProducts = () => {
        return this.typeahead.lookUpProducts({ prod_name: this.get!.prod_name });
    };

    public onProductSelected(event?: Item) {
        if (!this.get) return;

        this.get.prod_id = !!event ? '' + event.item_id : '';
        this.get.prod_name = event?.item_name ?? '';
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const { errorcode, errormessage } = await this.saveMaterialGrade(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public cancel() {
        this.submit.emit();
    }

    public async doDelete() {
        if (!this.get) return;

        try {
            this.deleting = true;
            const { errorcode, errormessage } = await this.deleteMaterialGrade(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getMaterialGrade(this.params);
            this.getLoaded.emit(this.get);
        }
    }

    private async getMaterialGrade(params: MaterialGradeParams): Promise<MaterialGradeGet> {
        return (await this.datatask.Post(3906, params))[0];
    }

    private async saveMaterialGrade(params: MaterialGradeGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3907, params))[0];
    }

    private async deleteMaterialGrade(params: MaterialGradeGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3908, params))[0];
    }

    private async init() {
        await this.loadGet();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state && 'materialgrade_keyno' in state.params) {
            this.params = state.params as unknown as MaterialGradeParams;
            state.setSize({ width: '60rem', height: '80rem' });
            await this.init();
            state.setHeading(this.get!.webpage_heading);
            this.submit.subscribe({ next: (value: ErrorMessageResponse | undefined) => state.close(value) });
        }
    }
}

export interface MaterialGradeParams {
    materialgrade_keyno: string;
}

export interface MaterialGradeGet {
    materialgrade_keyno: string;
    materialgrade_name: string;
    density_kg_pr_dm3: string | number;
    std_price_locval: string | number;
    prod_id: string;
    prod_name: string;
    webpage_heading: string;
}
